import { createAsyncThunk } from "@reduxjs/toolkit";
import { logout } from "./LoginSlice";
import { logoutUser } from "./AccountLogoutThunk";

/**
 * fetch all Referrals entries
 */
export const fetchReferralsItems = createAsyncThunk(
  "referrals/fetchItems",
  async (
    { page = 1, limit = 20, search = "", isLoadMore = false },
    thunkAPI
  ) => {
    const params = new URLSearchParams();
    params.append("page", page.toString());
    params.append("limit", limit.toString());
    params.append("approval_status", "not_pending");

    if (search) params.append("search", search);

    const res = await fetch(
      `${process.env.REACT_APP_API_URL}/api/referrals?${params.toString()}`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "x-user-role": "admin",
        },
      }
    );

    if (res.status === 401) {
      thunkAPI.dispatch(logout());
      thunkAPI.dispatch(logoutUser());
      window.location.href = "/";
      return thunkAPI.rejectWithValue("Unauthorized");
    }

    const json = await res.json();
    if (!res.ok) {
      throw new Error(json?.message || "Fetch failed");
    }

    return {
      ...json,
      isLoadMore,
    };
  }
);
export const fetchReferralsPendingItems = createAsyncThunk(
  "referrals/fetchItems",
  async (
    { page = 1, limit = 20, search = "", isLoadMore = false },
    thunkAPI
  ) => {
    const params = new URLSearchParams();
    params.append("page", page.toString());
    params.append("limit", limit.toString());
    params.append("approval_status", "pending");
    if (search) params.append("search", search);

    const res = await fetch(
      `${process.env.REACT_APP_API_URL}/api/referrals?${params.toString()}`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "x-user-role": "admin",
        },
      }
    );

    if (res.status === 401) {
      thunkAPI.dispatch(logout());
      thunkAPI.dispatch(logoutUser());
      window.location.href = "/";
      return thunkAPI.rejectWithValue("Unauthorized");
    }

    const json = await res.json();
    if (!res.ok) {
      throw new Error(json?.message || "Fetch failed");
    }

    return {
      ...json,
      isLoadMore,
    };
  }
);

export const fetchReferralById = createAsyncThunk(
  "referrals/fetchById",
  async (referralId, thunkAPI) => {
    const res = await fetch(
      `${process.env.REACT_APP_API_URL}/api/referrals/getByParam?_id=${referralId}`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "x-user-role": "admin",
        },
      }
    );

    if (res.status === 401) {
      thunkAPI.dispatch(logout());
      thunkAPI.dispatch(logoutUser());
      window.location.href = "/";
      return thunkAPI.rejectWithValue("Unauthorized");
    }

    const json = await res.json();
    if (!res.ok) {
      throw new Error(json?.message || "Failed to fetch referral");
    }

    return json;
  }
);
export const fetchReferralByMemberId = createAsyncThunk(
  "referrals/fetchByMemberId",
  async (memberID, thunkAPI) => {
    const res = await fetch(
      `${process.env.REACT_APP_API_URL}/api/referrals?member_id=${memberID}`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "x-user-role": "admin",
        },
      }
    );

    if (res.status === 401) {
      thunkAPI.dispatch(logout());
      thunkAPI.dispatch(logoutUser());
      window.location.href = "/";
      return thunkAPI.rejectWithValue("Unauthorized");
    }

    const json = await res.json();
    if (!res.ok) {
      throw new Error(json?.message || "Failed to fetch referral");
    }

    return json;
  }
);
export const fetchReferralByVendorId = createAsyncThunk(
  "referrals/fetchByVendorId",
  async (vendorID, thunkAPI) => {
    const res = await fetch(
      `${process.env.REACT_APP_API_URL}/api/referrals?vendor_id=${vendorID}`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "x-user-role": "admin",
        },
      }
    );

    if (res.status === 401) {
      thunkAPI.dispatch(logout());
      thunkAPI.dispatch(logoutUser());
      window.location.href = "/";
      return thunkAPI.rejectWithValue("Unauthorized");
    }

    const json = await res.json();
    if (!res.ok) {
      throw new Error(json?.message || "Failed to fetch referral");
    }

    return json;
  }
);

export const updateReferralsItems = createAsyncThunk(
  "referrals/updateItems",
  async (data, thunkAPI) => {
    const body = JSON.stringify(data);
    const res = await fetch(
      `${process.env.REACT_APP_API_URL}/api/referrals?_id=${data._id}`,
      {
        method: "PUT",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "x-user-role": "admin",
        },
        body: body,
      }
    );
    if (res.status === 401) {
      thunkAPI.dispatch(logout());
      thunkAPI.dispatch(logoutUser());
      window.location.href = "/";
      return thunkAPI.rejectWithValue("Unauthorized");
    }
    const json = await res.json();
    if (!res) {
      throw new Error(json?.message || "Fetch failed");
    }
    return json;
  }
);

export const fetchReferralHistoryItemsById = createAsyncThunk(
  "referral/history",
  async (referral_id, thunkAPI) => {
    const res = await fetch(
      `${process.env.REACT_APP_API_URL}/api/referralhistory?referral_id=${referral_id}`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "x-user-role": "admin",
        },
      }
    );
    if (res.status === 401) {
      thunkAPI.dispatch(logout());
      thunkAPI.dispatch(logoutUser());
      window.location.href = "/";
      return thunkAPI.rejectWithValue("Unauthorized");
    }
    const json = await res.json();
    if (!res) {
      throw new Error(json?.message || "Fetch failed");
    }
    return json;
  }
);

export const addReferralHistoryItem = createAsyncThunk(
  "referral/addHistory",
  async (data, thunkAPI) => {
    const body = JSON.stringify(data);
    const res = await fetch(
      `${process.env.REACT_APP_API_URL}/api/referralhistory`,
      {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "x-user-role": "admin",
        },
        body: body,
      }
    );
    if (res.status === 401) {
      thunkAPI.dispatch(logout());
      thunkAPI.dispatch(logoutUser());
      window.location.href = "/";
      return thunkAPI.rejectWithValue("Unauthorized");
    }
    const json = await res.json();
    if (!res) {
      throw new Error(json?.message || "Fetch failed");
    }
    return json;
  }
);

export const AcceptRefferal = createAsyncThunk(
  "refferal/accept",
  async ({ id, status }, thunkAPI) => {
    console.log("AcceptRefferal data:", id, status);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/referrals/${id}/review`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            "x-user-role": "admin",
          },
          body: JSON.stringify({status}),
        }
      );

      if (res.status === 401) {
        thunkAPI.dispatch(logout());
        thunkAPI.dispatch(logoutUser());
        window.location.href = "/";
        return thunkAPI.rejectWithValue("Unauthorized");
      }

      const json = await res.json();

      if (!res.ok) {
        // ✅ Reject with value instead of throwing
        return thunkAPI.rejectWithValue(json?.message || "Add failed");
      }

      return json;
    } catch (err) {
      // ✅ Network or unexpected error
      return thunkAPI.rejectWithValue(err.message || "Network error");
    }
  }
);
export const RejectedRefferal = createAsyncThunk(
  "refferal/reject",
  async ({ id, status,reject_reason }, thunkAPI) => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/referrals/${id}/review`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            "x-user-role": "admin",
          },
          body: JSON.stringify({status,reject_reason}),
        }
      );

      if (res.status === 401) {
        thunkAPI.dispatch(logout());
        thunkAPI.dispatch(logoutUser());
        window.location.href = "/";
        return thunkAPI.rejectWithValue("Unauthorized");
      }

      const json = await res.json();

      if (!res.ok) {
        // ✅ Reject with value instead of throwing
        return thunkAPI.rejectWithValue(json?.message || "Add failed");
      }

      return json;
    } catch (err) {
      // ✅ Network or unexpected error
      return thunkAPI.rejectWithValue(err.message || "Network error");
    }
  }
);
